import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchVisitTypes,
  createVisitType,
  updateVisitType,
  deleteVisitType,
  fetchVisitTypeChecklist,
  saveVisitTypeChecklist,
} from "../api/checklists";

// One shared empty array for the loading state. A fresh `[]` default is a new
// identity every render, which re-fires any effect/memo keyed on it — that is
// what looped the dashboard charts (UI audit, Oct 12 2026).
const EMPTY = [];

// The visit type list — the source for the visit form's type dropdown and for
// the Settings → Templates → Visit Types section. A visit type owns the
// checklist copied onto every visit of that type, and carries
// `requires_safety_sheet` (which replaced the per-visit `is_sitewalk` flag).
//
// Mirrors useJobTypes deliberately, right down to the query-key shape.
export function useVisitTypes({ includeInactive = false } = {}) {
  const queryClient = useQueryClient();
  const params = includeInactive ? { include_inactive: true } : {};

  const { data: visitTypes = EMPTY, isLoading } = useQuery({
    queryKey: ["visitTypes", { includeInactive }],
    queryFn: () => fetchVisitTypes(params),
    staleTime: 300_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["visitTypes"] });

  const createMutation = useMutation({ mutationFn: createVisitType, onSuccess: invalidate });
  const updateMutation = useMutation({
    mutationFn: ({ id, ...payload }) => updateVisitType(id, payload),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({ mutationFn: deleteVisitType, onSuccess: invalidate });

  return {
    visitTypes,
    isLoading,
    createVisitType: createMutation.mutateAsync,
    updateVisitType: updateMutation.mutateAsync,
    deleteVisitType: deleteMutation.mutateAsync,
    saving:
      createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
}

// One visit type's template, BOTH scopes. `enabled: false` while nothing is
// selected.
//
// ⚠️ Saving invalidates BOTH ["visitTypes"] (its task_count changes) and
// ["visits"] — a visit's own checklist rides along in its serialized payload,
// so a surface holding a stale ["visits"] cache would show the old task list.
// Same reasoning as the checklist mutations in ChecklistSection.
export function useVisitTypeChecklist(visitTypeId) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["visitTypes", visitTypeId, "checklist"],
    queryFn: () => fetchVisitTypeChecklist(visitTypeId),
    enabled: Boolean(visitTypeId),
  });

  // Payload for `saveChecklist`: `{ visit: [...], visit_closure: [...] }`.
  const saveMutation = useMutation({
    mutationFn: (payload) => saveVisitTypeChecklist(visitTypeId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["visitTypes"] }),
  });

  // ⚠️ MEMOIZED, and that is load-bearing, not tidiness. `data?.visit || []`
  // built a FRESH array on every render while `data` was undefined, so every
  // consumer with an effect keyed on this value — the Settings template editor
  // and VisitForm's re-seed — saw a changed dependency each render, called
  // setState, and re-rendered: "Maximum update depth exceeded", caught in a
  // browser rather than by the build, which compiles a render loop happily.
  //
  // `checklist` keeps the per-scope shape (mirrors useJobTypeChecklist, for
  // the Settings editor's two-section layout). `flatChecklist` merges both
  // scopes into one array for consumers that just want "everything this
  // type gives a new visit" (VisitForm/InlineVisitEntry's re-seed effect) —
  // each row already carries its own `.scope` from the server, so nothing
  // needs re-tagging here.
  const checklist = useMemo(
    () => ({ visit: data?.visit || [], visit_closure: data?.visit_closure || [] }),
    [data]
  );
  const flatChecklist = useMemo(
    () => [...checklist.visit, ...checklist.visit_closure],
    [checklist]
  );

  return {
    checklist,
    flatChecklist,
    isLoading,
    saveChecklist: saveMutation.mutateAsync,
    saving: saveMutation.isPending,
  };
}
